import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useReducedMotion } from "@/hooks/use-reduced-motion";
import { X } from "lucide-react";

const pillars = [
  {
    heading: "Direct Visibility",
    body: "Skip the algorithm. Recruiters browsing this directory are looking specifically for Riot experience, and your profile reaches them directly.",
  },
  {
    heading: "Complete Control",
    body: "Your profile, your rules. Show it to everyone, members only, or nobody. Toggle Open to Work on or off anytime, and it expires automatically after 30 days so a stale badge never speaks for you.",
  },
  {
    heading: "Everyone Here Is Verified",
    body: "Every profile is hand-verified before it appears, so you're never competing for attention with unverified accounts. It keeps the network real, and it's what makes it worth trusting.",
  },
];

interface AlumniValuePropsProps {
  dismissed: boolean;
  onDismiss: () => void;
}

export default function AlumniValueProps({ dismissed, onDismiss }: AlumniValuePropsProps) {
  const isReduced = useReducedMotion();
  const [visible, setVisible] = useState<boolean>(!dismissed);
  const [gone, setGone] = useState<boolean>(dismissed);

  useEffect(() => {
    if (!dismissed) {
      setGone(false);
      setVisible(true);
    }
  }, [dismissed]);

  if (gone) return null;

  const dismiss = () => {
    onDismiss();
    setVisible(false);
  };

  return (
    <motion.section
      initial={isReduced ? false : { opacity: 0, y: 12 }}
      animate={visible ? { opacity: 1, y: 0 } : { opacity: 0, y: isReduced ? 0 : -12 }}
      transition={{ duration: isReduced ? 0 : 0.35, ease: "easeInOut" }}
      className="w-full px-4"
    >
      <div className="container mx-auto max-w-6xl relative">
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          className="absolute top-2 right-2 z-10 w-6 h-6 rounded-full border border-primary/70 flex items-center justify-center text-primary/80 hover:border-primary hover:text-primary hover:bg-primary/10 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>

        <motion.div
          animate={
            visible
              ? { height: "auto", paddingBottom: 40 }
              : { height: 0, paddingBottom: 0 }
          }
          transition={{ duration: isReduced ? 0 : 0.35, ease: "easeInOut" }}
          onAnimationComplete={() => {
            if (!visible) setGone(true);
          }}
          style={{ overflow: "hidden" }}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {pillars.map((pillar) => (
              <div
                key={pillar.heading}
                className="bg-zinc-900/60 border border-white/[0.08] rounded-xl p-6 flex flex-col gap-3"
              >
                <div className="w-8 h-0.5 bg-primary rounded-full" />
                <h3 className="font-bold text-white text-base uppercase tracking-tight leading-snug" style={{ fontFamily: "var(--font-space-grotesk, 'Space Grotesk', sans-serif)" }}>
                  {pillar.heading}
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed font-sans">{pillar.body}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
}
